import React, { useState, useEffect } from 'react';
import { CargoTemperatureConfig, InhibitorEffectivenessEntry } from '../../../types/ViewModel/CargoTempLog/CargoTemperatureConfig';
import OdfjellPanel from '../../ui/odfjellPanel/OdfjellPanel';
import {
    FormLabel,
    FormRow,
    InhibitorFormWrapper,
    SettingsSection,
    EffectivenessEntriesContainer,
    EffectivenessEntryRow,
    EffectivenessEntryInputs,
    ActionButton,
    EntryDivider,
    FlexAutoContainer,
} from './CargoTempConfig.style';
import OdfjellInputComponent, { DateInputChangeData, InputChangeData } from '../../common/odfjell-input-component/OdfjellInputComponent';
import { DefaultWrapper } from '../../ui/odfjellPanel/OdfjellPanel.styles';
import useReadOnlyUtils from 'src/utils/useReadOnlyUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { inhibitorSelectOptions } from 'src/viewModels/cargoTempLog/inhibitorOptions';
import { getMissingFields, isConfigValid } from './CargoTempConfigUtils';

interface CargoTempQuestionnaireProps {
    config?: CargoTemperatureConfig;
    onClose: () => void;
    onSave: (updatedConfig: CargoTemperatureConfig) => void;
}

const CargoTempQuestionnaire: React.FC<CargoTempQuestionnaireProps> = ({ config, onClose, onSave }) => {
    const { checkIsCargoTempLogReadOnly } = useReadOnlyUtils();
    const isReadOnly = checkIsCargoTempLogReadOnly();

    // Maximum allowed effectiveness entries
    const MAX_EFFECTIVENESS_ENTRIES = 5;

    // Create a copy of the config to work with
    const [localConfig, setLocalConfig] = useState<CargoTemperatureConfig | undefined>(undefined);
    const [showValidation, setShowValidation] = useState<boolean>(false);

    useEffect(() => {
        if (config) {
            // Ensure inhibitorEffectivenessEntries is initialized
            const initialConfig = {
                ...config,
                inhibitorEffectivenessEntries: config.inhibitorEffectivenessEntries || [],
            };

            // If there are no entries and we're initializing, create a default entry
            if (initialConfig.inhibitorEffectivenessEntries.length === 0) {
                initialConfig.inhibitorEffectivenessEntries = [{ days: 0, temperature: 0 }];
            }
            setLocalConfig(initialConfig);
        }
    }, [config]);

    if (!localConfig) return null;

    const handleChange = (changeObject: InputChangeData) => {
        const { name, value } = changeObject.event.target as HTMLInputElement;

        // Handle checkbox values
        const updatedValue = changeObject.event.target.type === 'checkbox' ? changeObject.event.target.checked : value;

        setLocalConfig({
            ...localConfig,
            [name]: updatedValue,
        });
    };

    const handleDateChange = (changeObject: DateInputChangeData) => {
        const name = changeObject.name;
        const value = changeObject.date;

        setLocalConfig({
            ...localConfig,
            [name]: value,
        });
    };

    const handleEffectivenessEntryChange = (index: number, field: keyof InhibitorEffectivenessEntry, value: number) => {
        const updatedEntries = [...localConfig.inhibitorEffectivenessEntries];
        updatedEntries[index] = {
            ...updatedEntries[index],
            [field]: value,
        };

        setLocalConfig({
            ...localConfig,
            inhibitorEffectivenessEntries: updatedEntries,
        });
    };

    const addEffectivenessEntry = () => {
        // Don't add if we've reached the maximum limit
        if (localConfig.inhibitorEffectivenessEntries.length >= MAX_EFFECTIVENESS_ENTRIES) return;

        setLocalConfig({
            ...localConfig,
            inhibitorEffectivenessEntries: [...localConfig.inhibitorEffectivenessEntries, { days: 0, temperature: 0 }],
        });
    };

    const removeEffectivenessEntry = (index: number) => {
        // Don't remove if it's the last entry
        if (localConfig.inhibitorEffectivenessEntries.length <= 1) return;

        const updatedEntries = [...localConfig.inhibitorEffectivenessEntries];
        updatedEntries.splice(index, 1);

        setLocalConfig({
            ...localConfig,
            inhibitorEffectivenessEntries: updatedEntries,
        });
    };

    // utility functions moved to CargoTempConfigUtils.ts

    const handleSave = () => {
        if (!localConfig) return;
        const valid = isConfigValid(localConfig);
        if (!valid) {
            if (!showValidation) {
                setShowValidation(true);
                return; // first attempt: show missing fields and keep panel open
            }
            // second attempt: allow save even if incomplete
        }
        onSave(localConfig);
        onClose();
    };

    // Format the header title
    const headerTitle = `Inhibitor Questionnaire: ${config?.tradeName || 'Cargo'} (${config?.cargoNumber})`;

    return (
        <OdfjellPanel
            headerTitle={headerTitle}
            panelWidth={700}
            onCancel={onClose}
            onAction={handleSave}
            isActionDisabled={isReadOnly}
            actionButtonText="OK"
            showCancelButton={true}
            cancelButtonText="Cancel"
            hasCurtain={true}
            preventOutsideClose={true}
            defaultPosition="center"
        >
            <SettingsSection>
                <DefaultWrapper style={{ width: '100%' }}>
                    <h4>Inhibitor Information</h4>
                    <InhibitorFormWrapper>
                        <FormRow>
                            <FormLabel>Name of additive (inhibitor) present:</FormLabel>
                            <FlexAutoContainer>
                                <OdfjellInputComponent
                                    isReadOnly={isReadOnly}
                                    type="select"
                                    options={inhibitorSelectOptions}
                                    onChange={handleChange}
                                    value={localConfig.inhibitorType ?? ''}
                                    name="inhibitorType"
                                    disableUnit={true}
                                    selectDefaultOption="Select an inhibitor type"
                                />
                            </FlexAutoContainer>
                        </FormRow>

                        <FormRow>
                            <FormLabel>Amount of additive present:</FormLabel>
                            <FlexAutoContainer>
                                <OdfjellInputComponent
                                    isReadOnly={isReadOnly}
                                    type="number"
                                    onChange={handleChange}
                                    value={localConfig.inhibitorAmount || 0}
                                    name="inhibitorAmount"
                                    maxDecimals={0}
                                    unit="ppm"
                                />
                            </FlexAutoContainer>
                        </FormRow>

                        <FormRow>
                            <FormLabel>Is the additive Oxygen dependent:</FormLabel>
                            <FlexAutoContainer style={{ display: 'flex', justifyContent: 'flex-end', paddingRight: '40px' }}>
                                <OdfjellInputComponent
                                    isReadOnly={isReadOnly}
                                    type="checkbox"
                                    onChange={handleChange}
                                    value={localConfig.isOxygenDependent || false}
                                    name="isOxygenDependent"
                                    disableUnit={true}
                                />
                            </FlexAutoContainer>
                        </FormRow>

                        {localConfig.isOxygenDependent && (
                            <FormRow>
                                <FormLabel>Minimum level of Oxygen required:</FormLabel>
                                <FlexAutoContainer>
                                    <OdfjellInputComponent
                                        isReadOnly={isReadOnly}
                                        type="number"
                                        onChange={handleChange}
                                        value={localConfig.minimumOxygenLevel || 0}
                                        name="minimumOxygenLevel"
                                        unit="%"
                                    />
                                </FlexAutoContainer>
                            </FormRow>
                        )}

                        <FormRow>
                            <FormLabel>Date the additive was put in the product:</FormLabel>
                            <FlexAutoContainer>
                                <OdfjellInputComponent
                                    isReadOnly={isReadOnly}
                                    type="date"
                                    onDateInputChange={handleDateChange}
                                    value={localConfig.inhibitorAddedDate || ''}
                                    name="inhibitorAddedDate"
                                    disableUnit={true}
                                />
                            </FlexAutoContainer>
                        </FormRow>

                        <FormRow>
                            <FormLabel style={{ alignSelf: 'flex-start', marginTop: '8px' }}>Inhibitor effectiveness:</FormLabel>
                            <EffectivenessEntriesContainer>
                                {localConfig.inhibitorEffectivenessEntries.map((entry, index) => (
                                    <React.Fragment key={index}>
                                        {index > 0 && <EntryDivider />}
                                        <EffectivenessEntryRow>
                                            <EffectivenessEntryInputs>
                                                <OdfjellInputComponent
                                                    isReadOnly={isReadOnly}
                                                    type="number"
                                                    onChange={(data) =>
                                                        handleEffectivenessEntryChange(index, 'days', Number(data.event.target.value))
                                                    }
                                                    value={entry.days}
                                                    name={`effectiveDays-${index}`}
                                                    unit="days"
                                                    description="Effective for"
                                                />
                                                <OdfjellInputComponent
                                                    isReadOnly={isReadOnly}
                                                    type="number"
                                                    onChange={(data) =>
                                                        handleEffectivenessEntryChange(index, 'temperature', Number(data.event.target.value))
                                                    }
                                                    value={entry.temperature}
                                                    name={`effectiveTemperature-${index}`}
                                                    unit="°C"
                                                    description="at temperature"
                                                />
                                            </EffectivenessEntryInputs>
                                            <ActionButton
                                                type="button"
                                                onClick={() => removeEffectivenessEntry(index)}
                                                disabled={isReadOnly || localConfig.inhibitorEffectivenessEntries.length <= 1}
                                            >
                                                <FontAwesomeIcon icon="trash" style={{ marginRight: '5px' }} />
                                                Remove
                                            </ActionButton>
                                        </EffectivenessEntryRow>
                                    </React.Fragment>
                                ))}

                                {localConfig.inhibitorEffectivenessEntries.length < MAX_EFFECTIVENESS_ENTRIES && (
                                    <ActionButton
                                        type="button"
                                        onClick={addEffectivenessEntry}
                                        disabled={isReadOnly}
                                        style={{ alignSelf: 'flex-start', marginTop: '5px' }}
                                    >
                                        <FontAwesomeIcon icon="plus" style={{ marginRight: '5px' }} />
                                        Add Effectiveness Entry
                                    </ActionButton>
                                )}
                            </EffectivenessEntriesContainer>
                        </FormRow>

                        <FormRow>
                            <FormLabel>Action to be taken if voyage exceeds the effective lifetime:</FormLabel>
                            <FlexAutoContainer>
                                <OdfjellInputComponent
                                    isReadOnly={isReadOnly}
                                    type="textarea"
                                    onChange={handleChange}
                                    value={localConfig.inhibitorExpiryAction || ''}
                                    name="inhibitorExpiryAction"
                                    disableUnit={true}
                                    maxTextLength={200}
                                />
                            </FlexAutoContainer>
                        </FormRow>
                    </InhibitorFormWrapper>
                    {showValidation &&
                        (() => {
                            const missing = getMissingFields(localConfig);
                            return missing.length > 0 ? (
                                <div style={{ marginTop: '12px' }}>
                                    <div style={{ color: '#b00020', fontWeight: 600 }}>All fields are mandatory. Missing:</div>
                                    <ul style={{ marginTop: '6px', paddingLeft: '20px', color: '#b00020' }}>
                                        {missing.map((m, idx) => (
                                            <li key={`missing_${idx}`}>{m}</li>
                                        ))}
                                    </ul>
                                </div>
                            ) : null;
                        })()}
                </DefaultWrapper>
            </SettingsSection>
        </OdfjellPanel>
    );
};

export default CargoTempQuestionnaire;
